import { useEffect, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';

const accordionItems = [
  {
    title: 'Career Discovery & Readiness',
    content:
      'Programs designed to introduce aspiring technicians to opportunities in the collision repair industry through education, hands-on learning, and mentorship.',
  },
  {
    title: 'Mentor Development',
    content:
      'Supporting experienced professionals with tools and resources to mentor and guide the next generation of collision repair talent.',
  },
  {
    title: 'Career Placement & Retention',
    content:
      'Helping individuals transition into long-term careers through workforce partnerships, career readiness, and retention support.',
  },
  {
    title: 'Tool Scholarship',
    content:
      'Providing access to essential tools and equipment that help remove financial barriers for students and early-career technicians.',
  },
];

const missionSlides = [
  {
    title:
      'To strengthen the collision repair workforce by expanding opportunity, mentorship, and career pathways.',
  },
  {
    title:
      'Creating access, education, and support systems for the next generation of collision repair professionals.',
  },
];

export default function CIF() {
  const [openAccordion, setOpenAccordion] = useState(0);
  const [activeSlide, setActiveSlide] = useState(0);
  const [isDonateModalOpen, setIsDonateModalOpen] = useState(false);
  const missionSectionLabel = activeSlide === 0 ? 'Vision' : 'Mission';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!isDonateModalOpen) {
      return undefined;
    }

    const modalTimeout = window.setTimeout(() => {
      setIsDonateModalOpen(false);
    }, 6000);

    return () => window.clearTimeout(modalTimeout);
  }, [isDonateModalOpen]);

  const handleDonateClick = (event) => {
    event.preventDefault();
    setIsDonateModalOpen(true);
  };

  const nextSlide = () => {
    setActiveSlide((prev) => (prev === missionSlides.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setActiveSlide((prev) => (prev === 0 ? missionSlides.length - 1 : prev - 1));
  };

  return (
    <div
      className="min-h-screen bg-black text-white overflow-x-hidden"
      style={{ fontFamily: 'Blunt Conit, sans-serif' }}
    >
      {/* HERO */}
      <section
        className="relative min-h-[780px] lg:min-h-screen flex items-center justify-center overflow-hidden pt-[88px] sm:pt-[100px] lg:pt-[130px]"
      >
        {/* Video background */}
        <video
          className="absolute inset-0 w-full h-full object-cover"
          src={`${process.env.PUBLIC_URL}/assets/videos/HeroVideo-TEXTURE.mp4`}
          autoPlay
          loop
          muted
          playsInline
        />
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/1" />

        <div className="fixed inset-x-0 top-0 z-30 h-[88px] sm:h-[100px] lg:h-[130px] bg-black">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 h-full flex items-center justify-between gap-3 sm:gap-4 lg:gap-6">
            <img
              src={`${process.env.PUBLIC_URL}/assets/cf-logo-3.png`}
              alt="Champion It Forward"
              className="w-[120px] sm:w-[150px] lg:w-[220px] h-auto"
            />

            <div className="flex items-center gap-2 sm:gap-4 lg:gap-10 uppercase font-bold tracking-wide">
              <a href="#who-we-are" className="text-[12px] sm:text-[14px] lg:text-[24px]">Who We Are</a>
              <a href="#who-we-help" className="text-[12px] sm:text-[14px] lg:text-[24px]">Who We Help</a>
              <a href="#impact" className="text-[12px] sm:text-[14px] lg:text-[24px]">Impact</a>

              <button
                type="button"
                onClick={handleDonateClick}
                className="bg-[#ff1d25] px-3 sm:px-5 lg:px-8 py-0.5 text-[12px] sm:text-[14px] lg:text-[24px]"
              >
                Donate
              </button>
            </div>
          </div>
        </div>

        <div className="relative z-10 text-center px-6">
          {/* Logo above h1 on mobile only */}
          <img
            src={`${process.env.PUBLIC_URL}/assets/cf-logo-3.png`}
            alt="Champion It Forward"
            className="block sm:hidden mx-auto mb-8 w-[180px] h-auto"
          />
          <h1
            className="
              uppercase
              italic
              font-black
              leading-[1.0] tracking-[0em]
              text-[54px]
              sm:text-[82px]
              lg:text-[120px]
              xl:text-[150px]
              max-w-[1500px]
              mx-auto
            "
          >
            Driving The Collision Repair
            <br />
            Industry <span className="text-[#ff1d25]">Forward</span>
          </h1>
        </div>
      </section>

      {/* ABOUT */}
      <section id="who-we-are" className="bg-[#d9d9d9] text-black scroll-mt-[96px] sm:scroll-mt-[110px] lg:scroll-mt-[140px]">
        <div className="max-w-[1700px] mx-auto px-6 lg:px-20 py-16 md:py-24 lg:py-28">
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-24 items-center">
            <div className="flex flex-col justify-center h-full">
              <h2 className="uppercase text leading-[1.0] tracking-[0em] text-[26px] sm:text-[42px] md:text-[60px] max-w-6xl">
                Champion It Forward Is A Nonprofit
                <br />
                Focused On{' '}
                <span className="italic text-[#e51c22]">Strengthening</span>{' '}
                The Collision
                <br />
                Repair Workforce.
              </h2>

              <div className="mt-8 md:mt-10 space-y-6 font-['Roboto',sans-serif] text-[18px] md:text-[20px] text-neutral-700 leading-tight max-w-2xl">
                <p>
                  By investing in education, reducing financial barriers, and strengthening mentorship and apprenticeships, we
                  expand access to the trade and create lasting careers for the next generation of technicians.
                </p>

                <p>
                  This includes programs like STEP, a U.S. Department of Labor (DOL) Certified Apprenticeship Program, built to
                  create consistent, scalable pathways into the industry.
                </p>

                <p>
                  This work is designed to make a lasting impact for a strong workforce that&apos;s built with intention and shared
                  responsibility. Because when people are prepared, the industry moves forward.
                </p>
              </div>
            </div>

            <div className="w-full max-w-[568px] ml-auto">
              <img
                src={`${process.env.PUBLIC_URL}/assets/body-tech-1.png`}
                alt="Champion It Forward"
                className="relative z-10 w-full object-cover"
              />
            </div>
          </div>
        </div>

        <div className="h-3 bg-[#ff1d25]" />
      </section>

      {/* ACCORDION */}
      <section className="bg-black">
        <div className="max-w-[1100px] mx-auto px-6 py-14 md:py-20">
          <div className="space-y-6">
            {accordionItems.map((item, index) => {
              const isOpen = openAccordion === index;

              return (
                <div key={index} className="border-b border-white/10 pb-0">
                  <button
                    onClick={() => setOpenAccordion(isOpen ? -1 : index)}
                    className="w-full flex items-center justify-between uppercase text-left"
                  >
                    <span className="font-['Roboto',sans-serif] font-extrabold tracking-[-0.02em] text-[22px] sm:text-[28px] lg:text-[32px]">
                      {item.title}
                    </span>

                    <ChevronDown
                      className={`w-[32px] h-[32px] transition-transform duration-300 ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </button>

                  <div
                    className={`grid transition-all duration-300 ease-in-out ${
                      isOpen
                        ? 'grid-rows-[1fr] opacity-100 mt-5'
                        : 'grid-rows-[0fr] opacity-0'
                    }`}
                  >
                    <div className="overflow-hidden">
                      <p className="text-white font-['Roboto',sans-serif] text-[18px] sm:text-[22px] lg:text-[24px] leading-relaxed max-w-4xl">
                        {item.content}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHO IT HELPS */}
      <section id="who-we-help" className="bg-black pb-20 md:pb-32 scroll-mt-[96px] sm:scroll-mt-[110px] lg:scroll-mt-[140px]">
        <div
          className="relative overflow-hidden bg-cover bg-center"
          style={{ backgroundImage: "url('assets/img/cif/who-it-helps.png')" }}
        >
          <div className="grid lg:grid-cols-[520px_1fr]">
            <div className="relative flex items-center justify-center px-6 sm:px-10 py-[56px] sm:py-[79px] lg:translate-x-[180px] lg:w-[600px]">
              <h2 className="relative z-10 uppercase italic font-black text-white leading-[0.9] tracking-[0em] text-[78px] sm:text-[120px] lg:text-[240px]">
                Who It
                <br />
                Helps
              </h2>
            </div>

            <div className="px-6 sm:px-12 md:px-20 py-[40px] sm:py-[59px] flex flex-col justify-center gap-10 sm:gap-12 lg:translate-x-[300px]">
              <div>
                <h3 className="uppercase italic font-['Roboto_Condensed','Roboto',sans-serif] font-extrabold text-[26px] sm:text-[32px] leading-tight">
                  Aspiring Technicians
                </h3>

                <p className="mt-2 text-white font-['Roboto',sans-serif] leading-[1.2] tracking-[0em] font-normal text-[18px] sm:text-[24px] max-w-[42rem]">
                  Individuals exploring a career in collision repair who need access,
                  guidance, and a clear starting point.
                </p>
              </div>

              <div>
                <h3 className="uppercase italic font-['Roboto_Condensed','Roboto',sans-serif] font-extrabold text-[26px] sm:text-[32px] leading-tight">
                  Working Technicians
                </h3>

                <p className="mt-2 text-white font-['Roboto',sans-serif] leading-[1.2] tracking-[0em] font-normal text-[18px] sm:text-[24px] max-w-[42rem]">
                  Early-career professionals building skills, gaining experience,
                  and transitioning into long-term careers.
                </p>
              </div>

              <div>
                <h3 className="uppercase italic font-['Roboto_Condensed','Roboto',sans-serif] font-extrabold text-[26px] sm:text-[32px] leading-tight">
                  Industry Mentors
                </h3>

                <p className="mt-2 text-white font-['Roboto',sans-serif] leading-[1.2] tracking-[0em] font-normal text-[18px] sm:text-[24px] max-w-[42rem]">
                  Experienced technicians who help train, guide, and shape the next
                  generation of talent.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="relative bg-black py-20 sm:py-28 lg:py-36 overflow-hidden">
        <img
          src={`${process.env.PUBLIC_URL}/assets/img/cif/cif-mission-vehicle.png`}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/45 to-black/60" />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center">
            <p className="uppercase font-['Roboto',sans-serif] font-normal text-[18px] sm:text-[24px] text-white mb-6 sm:mb-8">
              {missionSectionLabel}
            </p>

            <h2 className="uppercase italic font-black leading-[1.0] tracking-[0em] text-[36px] sm:text-[58px] md:text-[92px] max-w-6xl mx-auto">
              {missionSlides[activeSlide].title
                .split(' ')
                .map((word, index) => (
                  <span
                    key={index}
                    className={
                      [
                        'strengthen',
                        'collision',
                        'repair',
                        'opportunity,',
                        'mentorship,',
                        'career',
                        'pathways.',
                      ].includes(word.toLowerCase())
                        ? 'text-[#ff1d25]'
                        : 'text-white'
                    }
                  >
                    {word}{' '}
                  </span>
                ))}
            </h2>
          </div>

          <button
            onClick={prevSlide}
            className="absolute left-2 sm:left-4 lg:left-[-100px] top-1/2 -translate-y-1/2"
          >
            <ChevronLeft className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 text-white" />
          </button>

          <button
            onClick={nextSlide}
            className="absolute right-2 sm:right-4 lg:right-[-100px] top-1/2 -translate-y-1/2"
          >
            <ChevronRight className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 text-white" />
          </button>

          <div className="flex justify-center gap-3 mt-16">
            {missionSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveSlide(index)}
                className={`w-3 h-3 rounded-full transition ${
                  activeSlide === index ? 'bg-white' : 'bg-white/30'
                }`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* IMPACT */}
      <section id="impact" className="bg-gradient-to-b from-black to-[#210000] py-14 sm:py-20 lg:py-24 scroll-mt-[96px] sm:scroll-mt-[110px] lg:scroll-mt-[140px]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid gap-6">
            <div className="group relative overflow-hidden rounded-md min-h-[280px] sm:min-h-[340px]">
              <img
                src={`${process.env.PUBLIC_URL}/assets/img/cif/cif-community-3.png`}
                alt=""
                className="absolute inset-0 w-full h-full object-cover object-[70%_center] sm:object-center transition duration-700 group-hover:scale-105"
              />

              <div className="absolute inset-0 bg-gradient-to-r from-red-700 via-red-600/30 to-transparent" />

              <div className="relative z-10 h-full flex items-center px-6 sm:px-12 py-8 sm:py-12">
                <div className="max-w-xl">
                  <h3 className="uppercase italic font-['Roboto_Condensed','Roboto',sans-serif] font-extrabold text-[40px] sm:text-[56px] leading-none">
                    Community
                  </h3>

                  <p className="mt-4 sm:mt-5 text-white font-['Roboto',sans-serif] text-[18px] sm:text-[24px]">
                    Bringing together partners, educators, and industry leaders
                    to strengthen the workforce and create lasting impact.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="group relative overflow-hidden rounded-md min-h-[280px] sm:min-h-[340px]">
                <img
                  src={`${process.env.PUBLIC_URL}/assets/img/cif/grant-3.png`}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover transition duration-700 group-hover:scale-105"
                />

                <div className="absolute inset-0 bg-gradient-to-r from-red-700 via-red-600/30 to-transparent" />

                <div className="relative z-10 h-full flex items-end p-6 sm:p-12">
                  <div>
                    <h3 className="uppercase italic font-['Roboto_Condensed','Roboto',sans-serif] font-extrabold text-[40px] sm:text-[56px] leading-none">
                      Grants
                    </h3>

                    <p className="mt-4 sm:mt-5 text-white font-['Roboto',sans-serif] text-[18px] sm:text-[24px]">
                      Funding programs that expand access to education,
                      training, and career opportunities.
                    </p>
                  </div>
                </div>
              </div>

              <div className="group relative overflow-hidden rounded-md min-h-[280px] sm:min-h-[340px]">
                <img
                  src={`${process.env.PUBLIC_URL}/assets/img/cif/grant-4.png`}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover transition duration-700 group-hover:scale-105"
                />

                <div className="absolute inset-0 bg-gradient-to-r from-red-700 via-red-600/30 to-transparent" />

                <div className="relative z-10 h-full flex items-end p-6 sm:p-12">
                  <div>
                    <h3 className="uppercase italic font-['Roboto_Condensed','Roboto',sans-serif] font-extrabold text-[40px] sm:text-[56px] leading-none">
                      Support
                    </h3>

                    <p className="mt-4 sm:mt-5 text-white font-['Roboto',sans-serif] text-[18px] sm:text-[24px]">
                      Providing mentorship, guidance, and real-world experience
                      to help individuals succeed.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[#E21717] rounded-md px-6 sm:px-10 py-8 sm:py-10 flex flex-col lg:flex-row items-center justify-between gap-6">
              <h3 className="uppercase italic font-['Roboto_Condensed','Roboto',sans-serif] font-extrabold text-[30px] sm:text-[38px] leading-[0.9] text-center lg:text-left lg:translate-x-[60px]">
                Be Part Of What Moves The Collision
                <br />
                Repair Industry Forward
              </h3>

              <button className="bg-black hover:bg-neutral-900 transition px-8 sm:px-12 py-2 uppercase font-['Anton',sans-serif] font-normal tracking-wide text-[20px] sm:text-[26px] lg:translate-x-[-30px]">
                Get Involved
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-black border-t border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 py-8 lg:h-[200px] lg:pt-8 flex flex-col lg:flex-row items-center lg:items-start justify-between gap-8">
          <img
            src={`${process.env.PUBLIC_URL}/assets/cf-logo-3.png`}
            alt="Champion It Forward"
            className="w-[180px] sm:w-[220px] lg:w-[280px] h-auto"
          />

          <div className="flex flex-wrap justify-center lg:justify-end items-center gap-3 sm:gap-6 lg:gap-10 uppercase font-bold tracking-wide">
            <a href="#who-we-are" className="text-[14px] sm:text-[18px] lg:text-[24px]">Who We Are</a>
            <a href="#who-we-help" className="text-[14px] sm:text-[18px] lg:text-[24px]">Who We Help</a>
            <a href="#impact" className="text-[14px] sm:text-[18px] lg:text-[24px]">Impact</a>

            <button
              type="button"
              onClick={handleDonateClick}
              className="bg-[#ff1d25] px-5 sm:px-7 lg:px-8 py-0.5 text-[14px] sm:text-[18px] lg:text-[24px]"
            >
              Donate
            </button>
          </div>
        </div>
      </footer>

      {isDonateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="donate-modal-title"
            className="relative z-10 w-full max-w-lg border border-white/10 bg-[#111111] px-8 py-10 text-center shadow-2xl"
          >
            <p className="mb-3 text-sm uppercase tracking-[0.35em] text-[#ff1d25]">
              Champion It Forward
            </p>
            <h2 id="donate-modal-title" className="text-3xl sm:text-4xl font-black uppercase">
              Donations Portal Coming Soon
            </h2>
            <p className="mt-4 text-base sm:text-lg text-white/75 font-['Roboto',sans-serif]">
              Thanks for your interest. This donation experience will be available soon.
            </p>
          </div>
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Anton&display=swap');

        @font-face {
          font-family: 'Roboto';
          src: url('assets/font/Roboto-Regular.ttf') format('truetype');
          font-weight: 400;
          font-style: normal;
          font-display: swap;
        }

        @font-face {
          font-family: 'Roboto';
          src: url('assets/font/Roboto-Italic.ttf') format('truetype');
          font-weight: 400;
          font-style: italic;
          font-display: swap;
        }

        @font-face {
          font-family: 'Roboto';
          src: url('assets/font/Roboto-Medium.ttf') format('truetype');
          font-weight: 500;
          font-style: normal;
          font-display: swap;
        }

        @font-face {
          font-family: 'Roboto';
          src: url('assets/font/Roboto-MediumItalic.ttf') format('truetype');
          font-weight: 500;
          font-style: italic;
          font-display: swap;
        }

        @font-face {
          font-family: 'Roboto';
          src: url('assets/font/Roboto-Bold.ttf') format('truetype');
          font-weight: 700;
          font-style: normal;
          font-display: swap;
        }

        @font-face {
          font-family: 'Roboto';
          src: url('assets/font/Roboto-BoldItalic.ttf') format('truetype');
          font-weight: 700;
          font-style: italic;
          font-display: swap;
        }

        @font-face {
          font-family: 'Blunt Conit';
          src: url('assets/font/Fontspring-DEMO-blunt-con.otf') format('opentype');
          font-weight: 400;
          font-style: normal;
          font-display: swap;
        }

        @font-face {
          font-family: 'Blunt Conit';
          src: url('assets/font/Fontspring-DEMO-blunt-conit.otf') format('opentype');
          font-weight: 400;
          font-style: italic;
          font-display: swap;
        }

        @font-face {
          font-family: 'Blunt Conit';
          src: url('assets/font/Fontspring-DEMO-blunt-con.otf') format('opentype');
          font-weight: 700;
          font-style: normal;
          font-display: swap;
        }

        @font-face {
          font-family: 'Blunt Conit';
          src: url('assets/font/Fontspring-DEMO-blunt-conit.otf') format('opentype');
          font-weight: 700;
          font-style: italic;
          font-display: swap;
        }

        @font-face {
          font-family: 'Blunt Conit';
          src: url('assets/font/Fontspring-DEMO-blunt-con.otf') format('opentype');
          font-weight: 900;
          font-style: normal;
          font-display: swap;
        }

        @font-face {
          font-family: 'Blunt Conit';
          src: url('assets/font/Fontspring-DEMO-blunt-conit.otf') format('opentype');
          font-weight: 900;
          font-style: italic;
          font-display: swap;
        }

        .clip-diagonal {
          clip-path: polygon(18% 0%, 100% 0%, 100% 100%, 0% 100%);
        }
      `}</style>
    </div>
  );
}